import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Heart, MessageCircle, Lock, User, LogOut, CreditCard, Menu, X, Shield } from 'lucide-react';

const Header = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <header className="glass-header border-b border-stone-200 fixed top-0 left-0 right-0 z-50">
      <div className="content-container">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2" data-testid="header-logo">
            <Heart className="h-7 w-7 text-[#6B8E7B]" fill="#6B8E7B" />
            <div className="hidden sm:block">
              <span className="text-xl font-bold font-['Outfit'] text-stone-800">NurseCommunity</span>
              <span className="text-xs text-stone-400 block -mt-1">by Gina Rena</span>
            </div>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            <Link to="/" data-testid="nav-home">
              <Button variant="ghost" className="rounded-full text-stone-600 hover:text-stone-800 hover:bg-stone-100">
                Feed
              </Button>
            </Link>
            <Link to="/messages" data-testid="nav-messages">
              <Button variant="ghost" className="rounded-full text-stone-600 hover:text-stone-800 hover:bg-stone-100">
                <MessageCircle className="h-4 w-4 mr-2" />
                Messages
              </Button>
            </Link>
            <Link to="/anonymous" data-testid="nav-anonymous">
              <Button variant="ghost" className="rounded-full text-stone-600 hover:text-stone-800 hover:bg-stone-100">
                <Lock className="h-4 w-4 mr-2" />
                Anonymous
                {user?.is_premium && <span className="ml-2 premium-badge">PRO</span>}
              </Button>
            </Link>
          </nav>

          {/* User Menu */}
          <div className="flex items-center gap-3">
            {!user?.is_premium && (
              <Link to="/subscribe" data-testid="subscribe-link">
                <Button className="btn-premium hidden sm:flex">
                  <Lock className="h-4 w-4 mr-2" />
                  Go Premium
                </Button>
              </Link>
            )}

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="rounded-full p-0" data-testid="user-menu-button">
                  <Avatar className="h-9 w-9">
                    <AvatarImage src={user?.avatar_url} />
                    <AvatarFallback className="bg-[#6B8E7B] text-white text-sm">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 rounded-xl">
                <div className="px-3 py-2">
                  <p className="font-medium text-stone-800">{user?.name}</p>
                  <p className="text-sm text-stone-500">{user?.email}</p>
                  {user?.is_premium && <span className="premium-badge mt-1 inline-block">Premium</span>}
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                  <User className="h-4 w-4 mr-2" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/subscribe')} data-testid="menu-subscription">
                  <CreditCard className="h-4 w-4 mr-2" />
                  Subscription
                </DropdownMenuItem>
                {user?.role === 'admin' && (
                  <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                    <Shield className="h-4 w-4 mr-2" />
                    Admin Dashboard
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              className="md:hidden rounded-full p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-stone-200 py-4 animate-fade-in">
            <nav className="flex flex-col gap-2">
              <Link to="/" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="w-full justify-start rounded-xl">Feed</Button>
              </Link>
              <Link to="/messages" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="w-full justify-start rounded-xl">
                  <MessageCircle className="h-4 w-4 mr-2" />
                  Messages
                </Button>
              </Link>
              <Link to="/anonymous" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="w-full justify-start rounded-xl">
                  <Lock className="h-4 w-4 mr-2" />
                  Anonymous
                </Button>
              </Link>
              {!user?.is_premium && (
                <Link to="/subscribe" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="btn-premium w-full mt-2">
                    <Lock className="h-4 w-4 mr-2" />
                    Go Premium - €5/month
                  </Button>
                </Link>
              )}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
